/**
 * A component to represent a single file or directory
 *
 * @author Daniel Lee (dlee@yext.com)
 */
import {TreeNodeShape} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/shapesJsDoc';
import {DirectoryTreeNode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/DirectoryTreeNode';
import {FileTreeNode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/FileTreeNode';
import {NodeType} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/NodeConstants';

/**
 * @param {{
 *   nodeId: string,
 *   nodeIdToNode: !Map<string, TreeNodeShape>,
 *   selectedFileNodeIds: !Set<string>,
 *   expandedNodeIds: !Set<string>,
 *   modifiedFileNodeIds: (!Set<string> | undefined),
 *   focusedFileNodeId: (string | undefined),
 *   treeActionsMode: (string | undefined),
 *   hoveredRowNodeId: (string | undefined),
 *   isRootNode: boolean,
 *   treeSelectMode: string,
 *   onExpandDirectory: !function(string),
 *   onHoverRow: !function({current: {getBoundingClientRect: !Function}}, string),
 *   onDoubleClickDirectory: (function(string) | undefined),
 *   onBulkSelectDirectory: (function(string, boolean) | undefined),
 *   onBulkSelectFile: (function(string, boolean) | undefined),
 *   onClickFile: !function(TreeNodeShape),
 * }} props
 * @return {React.ReactNode}
 */
export const TreeNode = React.memo(props => {
  const {
    nodeId,
    nodeIdToNode,
    selectedFileNodeIds,
    expandedNodeIds,
    modifiedFileNodeIds,
    focusedFileNodeId,
    treeActionsMode,
    isRootNode,
    treeSelectMode,
    onHoverRow,
    onExpandDirectory,
    onDoubleClickDirectory,
    onBulkSelectDirectory,
    onBulkSelectFile,
    onClickFile,
  } = props;

  const node = nodeIdToNode.get(nodeId);
  const actionMenuContainerRef = React.useRef();

  /**
   * @param {string} nodeId
   * @return {boolean}
   */
  const isFocused = nodeId => {
    return focusedFileNodeId
      ? focusedFileNodeId === nodeId
      : selectedFileNodeIds.has(nodeId);
  };

  const handleOnHoverRow = React.useCallback(
    nodeId => {
      onHoverRow(actionMenuContainerRef, nodeId);
    },
    [actionMenuContainerRef, onHoverRow]);

  const handleOnExpand = React.useCallback(
    () => !isRootNode && onExpandDirectory(nodeId),
    [isRootNode, nodeId, onExpandDirectory]);

  if (!node) {
    return null;
  }

  const renderChildren = () => {
    if (node.children) {
      const files = [];
      const directories = [];

      Array.from(node.children.values())
        .filter(childPath => nodeIdToNode.has(childPath))
        .forEach(childPath => {
          const childNode = nodeIdToNode.get(childPath);
          isDirectory(childNode) ? directories.push(childPath) : files.push(childPath);
        });

      const nodeSortFunction = (a, b) => {
        const aName = nodeIdToNode.get(a) ? nodeIdToNode.get(a).displayName : '';
        const bName = nodeIdToNode.get(b) ? nodeIdToNode.get(b).displayName : '';
        return aName.toLowerCase().localeCompare(bName.toLowerCase());
      };

      const renderChild = childId => {
        return (
          <TreeNode
            key={childId}
            nodeId={childId}
            nodeIdToNode={nodeIdToNode}
            selectedFileNodeIds={selectedFileNodeIds}
            expandedNodeIds={expandedNodeIds}
            modifiedFileNodeIds={modifiedFileNodeIds}
            focusedFileNodeId={focusedFileNodeId}
            treeActionsMode={treeActionsMode}
            isRootNode={false}
            treeSelectMode={treeSelectMode}
            onExpandDirectory={onExpandDirectory}
            onHoverRow={onHoverRow}
            onDoubleClickDirectory={onDoubleClickDirectory}
            onBulkSelectDirectory={onBulkSelectDirectory}
            onBulkSelectFile={onBulkSelectFile}
            onClickFile={onClickFile}
          />
        );
      };

      return (
        <>
          {files.sort(nodeSortFunction).map(renderChild)}
          {directories.sort(nodeSortFunction).map(renderChild)}
        </>
      );
    }
  };

  switch (node.type) {
    case NodeType.DIRECTORY:
      const isExpanded = expandedNodeIds.has(node.id);
      return (
        <DirectoryTreeNode
          node={node}
          onHoverRow={handleOnHoverRow}
          isRootNode={isRootNode}
          treeSelectMode={treeSelectMode}
          isExpanded={expandedNodeIds.has(node.id)}
          onExpand={handleOnExpand}
          onDoubleClick={onDoubleClickDirectory
            ? () => onDoubleClickDirectory(nodeId)
            : null}
          onBulkSelect={onBulkSelectDirectory
            ? () => onBulkSelectDirectory(nodeId, !node.isBulkSelected)
            : null}
          rowRef={actionMenuContainerRef}
          treeActionsMode={treeActionsMode}
        >
          {(isRootNode || isExpanded) && renderChildren()}
        </DirectoryTreeNode>
      );
    case NodeType.FILE:
      return (
        <FileTreeNode
          node={node}
          selected={selectedFileNodeIds.has(nodeId)}
          focused={isFocused(nodeId)}
          modified={!!modifiedFileNodeIds && modifiedFileNodeIds.has(nodeId)}
          treeActionsMode={treeActionsMode}
          treeSelectMode={treeSelectMode}
          rowRef={actionMenuContainerRef}
          onHoverRow={nodeId => onHoverRow(actionMenuContainerRef, nodeId)}
          onBulkSelect={
            onBulkSelectFile
              ? () => onBulkSelectFile(node.id, !node.isBulkSelected)
              : null}
          onClick={() => onClickFile(node)}
        />
      );
  }
  return null;
});

/**
 * @param {!TreeNodeShape} node
 * @return {boolean}
 */
const isDirectory = node => node && node.type === NodeType.DIRECTORY;
